/**
 * Single stream workspace: breadcrumbs, KPI header, tabs for prospects/pipelines/jobs/settings/emails.
 *
 * Where: `/streams/:streamId` (see router).
 * Uses: `streamApi`, tab components from `@/components/stream`, `EmailsList`, charts as configured.
 * Behavior: keeps tab state in URL or local state per implementation; refetches on stream id change.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  Replay as ReplayIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { streamApi } from '@/api/stream';
import { useAuth } from '@/hooks/useAuth';
import {
  ProspectPipelineList,
  StreamPipelinesTab,
  StreamStatistics,
  StreamSettings,
  EmailsList,
  ActionLimitsControl,
} from '@/components';
import { ProspectEventsLinearChart } from '@/components/charts/ProspectEventsLinearChart';
import { ImportLeadsModal } from '@/components/stream/ImportLeadsModal';
import { StreamJobsList } from '@/components/stream/StreamJobsList';
import ScopedSignalCatalog from '@/components/ScopedSignalCatalog';
import { Button } from '@mui/material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stream-tabpanel-${index}`}
      aria-labelledby={`stream-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const StreamDetails: React.FC = () => {
  const { streamId } = useParams<{ streamId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  
  // Initialize activeTab from URL params, default to 0
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 6) {
        return tabIndex;
      }
    }
    return 0;
  });
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isRetryFailedDialogOpen, setIsRetryFailedDialogOpen] = useState(false);
  const [isWithdrawInvitesDialogOpen, setIsWithdrawInvitesDialogOpen] = useState(false);

  const retryFailedProspectsMutation = useMutation({
    mutationFn: () => streamApi.retryStreamFailedProspectPipelines(streamId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['prospect-pipelines', streamId],
      });
    },
    onError: (error: any) => {
      console.error('Failed to retry failed prospect pipelines for stream:', error);
    },
  });

  const withdrawPendingInvitesMutation = useMutation({
    mutationFn: () => streamApi.withdrawPendingInvites(streamId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['jobs', streamId],
      });
    },
    onError: (error: any) => {
      console.error('Failed to withdraw pending invites for stream:', error);
    },
  });
  
  // Fetch stream details
  // Include user key in query key to ensure proper data refresh on impersonation
  const {
    data: stream,
    isLoading: isLoadingStream,
    error: streamError,
  } = useQuery({
    queryKey: ['stream', user?.key, streamId],
    queryFn: () => streamApi.getStreamDetails(streamId!),
    enabled: !!streamId,
  });

  // Update URL when tab changes
  const updateURLTab = useCallback((tabIndex: number) => {
    setSearchParams((currentSearchParams) => {
      const newSearchParams = new URLSearchParams(currentSearchParams);

      if (tabIndex === 0) {
        // Remove tab param if it's the default (first tab)
        newSearchParams.delete('tab');
      } else {
        newSearchParams.set('tab', tabIndex.toString());
      }

      return newSearchParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Sync state from URL params when URL changes externally (e.g., browser back button)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    let urlTabIndex = 0;
    if (tabParam !== null) {
      const parsedTab = parseInt(tabParam, 10);
      if (!isNaN(parsedTab) && parsedTab >= 0 && parsedTab <= 7) {
        urlTabIndex = parsedTab;
      }
    }

    if (urlTabIndex !== activeTab) {
      setActiveTab(urlTabIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    updateURLTab(newValue);
  };


  if (isLoadingStream) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (streamError || !stream) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load stream details. Please try again.
        </Alert>
      </Box>
    );
  }


  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Breadcrumbs */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
            <Link
              color="inherit"
              href="/experiments"
              onClick={(e) => {
                e.preventDefault();
                navigate('/experiments');
              }}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Experiments
            </Link>
            <Typography color="text.primary">{stream.name}</Typography>
          </Breadcrumbs>
        </Box>

        {/* Stream Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isMobile && (
              <IconButton onClick={() => navigate('/experiments')} edge="start">
                <ArrowBackIcon />
              </IconButton>
            )}
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              {stream.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<ReplayIcon />}
              onClick={() => setIsRetryFailedDialogOpen(true)}
              disabled={retryFailedProspectsMutation.isPending}
            >
              {retryFailedProspectsMutation.isPending ? 'Retrying failed leads…' : 'Retry failed leads'}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<PersonRemoveIcon />}
              onClick={() => setIsWithdrawInvitesDialogOpen(true)}
              disabled={withdrawPendingInvitesMutation.isPending}
            >
              {withdrawPendingInvitesMutation.isPending ? 'Withdrawing invites…' : 'Withdraw pending invites'}
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setIsImportModalOpen(true)}
              sx={{ ml: 'auto' }}
            >
              Add Leads
            </Button>
          </Box>
        </Box>

        {/* Stream Info: Creator, Operator, Team */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
          {stream.creator && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                cursor: 'pointer',
                '&:hover': { opacity: 0.7 }
              }}
              onClick={() => stream.creator && navigate(`/users/${stream.creator.key}`)}
            >
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Creator:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                {stream.creator.name || stream.creator.email}
              </Typography>
            </Box>
          )}
          {stream.operator && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                cursor: 'pointer',
                '&:hover': { opacity: 0.7 }
              }}
              onClick={() => stream.operator && navigate(`/users/${stream.operator.key}`)}
            >
              <GroupIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Operator:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                {stream.operator.name || stream.operator.email}
              </Typography>
            </Box>
          )}
          {stream.team && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                cursor: 'pointer',
                '&:hover': { opacity: 0.7 }
              }}
              onClick={() => stream.team && navigate(`/teams/${stream.team.key}`)}
            >
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Team:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                {stream.team.name}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Stream Statistics */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <StreamStatistics stream={stream} />
      </Box>

      {/* Tabs */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: '1000px' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ px: 3 }}>
            <Tab label="Leads" />
            <Tab label="Emails" />
            <Tab label="Settings" />
            <Tab label="Statistics" />
            <Tab label="Pipelines" />
            <Tab label="Jobs" />
            <Tab label="Limits & Activity Control" />
            <Tab label="Signal Catalog" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 0.5 }}>
          <TabPanel value={activeTab} index={0}>
            <ProspectPipelineList streamId={streamId!} />
          </TabPanel>
          
          <TabPanel value={activeTab} index={1}>
            {streamId && (
              <Box sx={{ p: 0 }}>
                <EmailsList streamId={streamId} />
              </Box>
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <StreamSettings stream={stream} />
          </TabPanel>
          
          <TabPanel value={activeTab} index={3}>
            {/* Statistics Tab */}
            {streamId && (
              <Box sx={{ p: 3 }}>
                <ProspectEventsLinearChart 
                  streamUuid={streamId} 
                  height={360}
                  showRangeTabs={true}
                />
              </Box>
            )}
          </TabPanel>
          
          <TabPanel value={activeTab} index={4}>
            {streamId && (
              <Box sx={{ p: 3 }}>
                <StreamPipelinesTab streamId={streamId} />
              </Box>
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={5}>
            {streamId && <StreamJobsList streamUuid={streamId} />}
          </TabPanel>

          <TabPanel value={activeTab} index={6}>
            {streamId && (
              <Box sx={{ p: 2 }}>
                <ActionLimitsControl scope="stream" resourceId={streamId} />
              </Box>
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={7}>
            {streamId && (
              <Box sx={{ p: 2, width: '100%' }}>
                <ScopedSignalCatalog
                  scopeType="stream"
                  scopeUuid={streamId}
                  fallbackUserUuid={stream.operator?.key}
                  fallbackUserLabel={stream.operator?.name || stream.operator?.email || undefined}
                />
              </Box>
            )}
          </TabPanel>
        </Box>
      </Box>
      
      {/* Import Leads Modal */}
      {streamId && (
        <ImportLeadsModal
          open={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          streamUuid={streamId}
        />
      )}

      <Dialog
        open={isRetryFailedDialogOpen}
        onClose={() => setIsRetryFailedDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Retry failed leads</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Are you sure you want to retry all failed leads in this experiment? This will reschedule
            them to run again later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRetryFailedDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<ReplayIcon />}
            onClick={() => {
              retryFailedProspectsMutation.mutate();
              setIsRetryFailedDialogOpen(false);
            }}
            disabled={retryFailedProspectsMutation.isPending}
          >
            Retry failed leads
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isWithdrawInvitesDialogOpen}
        onClose={() => setIsWithdrawInvitesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Withdraw pending invites</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Are you sure you want to withdraw all pending invites for this experiment? This will
            schedule a job to process them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsWithdrawInvitesDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<PersonRemoveIcon />}
            onClick={() => {
              withdrawPendingInvitesMutation.mutate();
              setIsWithdrawInvitesDialogOpen(false);
            }}
            disabled={withdrawPendingInvitesMutation.isPending}
          >
            Withdraw pending invites
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
